import { useState, useRef, useEffect, useCallback } from 'react';

const DEFAULT_MAX_ENTRIES = 50;

function deepClone(value) {
  if (value === undefined || value === null) return value;
  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
  } catch {
    /* fall through */
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

/**
 * Production-grade undo/redo for Assessment Builder canvas state:
 * screens, current screen tab index, and navigation (skip) rules.
 *
 * - Deep-cloned snapshots with structuredClone (JSON fallback).
 * - Bounded history (default 50 steps each direction).
 * - use runWithoutUndo() for API load, server sync, save dedupe, version restore.
 * - use beginInteractionBatch / endInteractionBatch around drag and live resize
 *   so intermediate pointer moves do not flood the stack (one undo step per gesture).
 *   Nesting is supported (depth ref): only the outermost pair records one snapshot.
 */
export function useAssessmentBuilderUndoRedo({
  screens,
  currentScreenIndex,
  navigationRules,
  setScreensBase,
  setCurrentScreenIndexBase,
  setNavigationRulesBase,
  maxEntries = DEFAULT_MAX_ENTRIES,
}) {
  /** @type {React.MutableRefObject<Array<{ screens: unknown, currentScreenIndex: number, navigationRules: unknown }>>} */
  const pastRef = useRef([]);
  /** @type {React.MutableRefObject<Array<{ screens: unknown, currentScreenIndex: number, navigationRules: unknown }>>} */
  const futureRef = useRef([]);
  const [stackVersion, setStackVersion] = useState(0);

  const bumpStacks = useCallback(() => {
    setStackVersion((v) => v + 1);
  }, []);

  const screensRef = useRef(screens);
  const indexRef = useRef(currentScreenIndex);
  const rulesRef = useRef(navigationRules);

  const suppressDepthRef = useRef(0);
  /** Nested drag/resize: only outermost begin/end pair pushes one snapshot and gates recording. */
  const interactionBatchDepthRef = useRef(0);

  useEffect(() => {
    screensRef.current = screens;
  }, [screens]);
  useEffect(() => {
    indexRef.current = currentScreenIndex;
  }, [currentScreenIndex]);
  useEffect(() => {
    rulesRef.current = navigationRules;
  }, [navigationRules]);

  const snapshotFromRefs = useCallback(() => ({
    screens: deepClone(screensRef.current),
    currentScreenIndex: indexRef.current,
    navigationRules: deepClone(rulesRef.current),
  }), []);

  const shouldRecord = useCallback(() => {
    return (
      suppressDepthRef.current === 0 && interactionBatchDepthRef.current === 0
    );
  }, []);

  const pushSnapshot = useCallback(() => {
    if (!shouldRecord()) return;
    const snap = snapshotFromRefs();
    const p = pastRef.current;
    pastRef.current = [...p.slice(-(maxEntries - 1)), snap];
    futureRef.current = [];
    bumpStacks();
  }, [shouldRecord, snapshotFromRefs, maxEntries, bumpStacks]);

  const setScreens = useCallback(
    (update) => {
      if (shouldRecord()) {
        pushSnapshot();
      }
      setScreensBase(update);
    },
    [setScreensBase, shouldRecord, pushSnapshot]
  );

  const setCurrentScreenIndex = useCallback(
    (update) => {
      if (shouldRecord()) {
        pushSnapshot();
      }
      setCurrentScreenIndexBase(update);
    },
    [setCurrentScreenIndexBase, shouldRecord, pushSnapshot]
  );

  const setNavigationRules = useCallback(
    (update) => {
      if (shouldRecord()) {
        pushSnapshot();
      }
      setNavigationRulesBase(update);
    },
    [setNavigationRulesBase, shouldRecord, pushSnapshot]
  );

  const runWithoutUndo = useCallback((fn) => {
    suppressDepthRef.current += 1;
    try {
      fn();
    } finally {
      suppressDepthRef.current = Math.max(0, suppressDepthRef.current - 1);
    }
  }, []);

  const beginInteractionBatch = useCallback(() => {
    if (interactionBatchDepthRef.current === 0 && shouldRecord()) {
      pushSnapshot();
    }
    interactionBatchDepthRef.current += 1;
  }, [shouldRecord, pushSnapshot]);

  const endInteractionBatch = useCallback(() => {
    interactionBatchDepthRef.current = Math.max(
      0,
      interactionBatchDepthRef.current - 1
    );
  }, []);

  const clearHistory = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    interactionBatchDepthRef.current = 0;
    bumpStacks();
  }, [bumpStacks]);

  const undo = useCallback(() => {
    const p = pastRef.current;
    if (p.length === 0) return;
    interactionBatchDepthRef.current = 0;
    const currentSnap = snapshotFromRefs();
    const prevSnap = p[p.length - 1];
    pastRef.current = p.slice(0, -1);
    futureRef.current = [currentSnap, ...futureRef.current].slice(0, maxEntries);
    setScreensBase(prevSnap.screens);
    setCurrentScreenIndexBase(prevSnap.currentScreenIndex);
    setNavigationRulesBase(prevSnap.navigationRules);
    bumpStacks();
  }, [
    snapshotFromRefs,
    maxEntries,
    setScreensBase,
    setCurrentScreenIndexBase,
    setNavigationRulesBase,
    bumpStacks,
  ]);

  const redo = useCallback(() => {
    const f = futureRef.current;
    if (f.length === 0) return;
    interactionBatchDepthRef.current = 0;
    const currentSnap = snapshotFromRefs();
    const nextSnap = f[0];
    futureRef.current = f.slice(1);
    pastRef.current = [...pastRef.current, currentSnap].slice(-maxEntries);
    setScreensBase(nextSnap.screens);
    setCurrentScreenIndexBase(nextSnap.currentScreenIndex);
    setNavigationRulesBase(nextSnap.navigationRules);
    bumpStacks();
  }, [
    snapshotFromRefs,
    maxEntries,
    setScreensBase,
    setCurrentScreenIndexBase,
    setNavigationRulesBase,
    bumpStacks,
  ]);

  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;
  void stackVersion;

  return {
    setScreens,
    setCurrentScreenIndex,
    setNavigationRules,
    undo,
    redo,
    canUndo,
    canRedo,
    runWithoutUndo,
    beginInteractionBatch,
    endInteractionBatch,
    clearHistory,
  };
}

export default useAssessmentBuilderUndoRedo;
